import client from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import paypal from 'braintree-web/paypal';
import threeDSecure from 'braintree-web/three-d-secure';
import find from 'lodash/find';
import get from 'lodash/get';
import ReactCookies from 'react-cookies';
import { CURRENCY_CONFIG, MERCHANT_NAME } from '../brands/default/config';
import {
  COOKIES,
  PAYMENT_METHODS,
  PAYMENT_TYPES,
} from '../brands/default/constants';
import {
  ERROR_MESSAGE,
  LIABILITY_ERROR,
  NO_EXPRESS_BUY,
  TRY_CHECKOUT,
  GENERIC_ERROR,
  PROGRESS_MESSAGE_WAIT,
  PROGRESS_MESSAGE_PAY,
  PROGRESS_MESSAGE_SUCCESS,
  PROGRESS_MESSAGE_FAILED,
} from '../brands/default/content/helpers';
import { logToSplunk } from '../components/_generic/axiosSplunk/axiosSplunk';
import { trackPurchase } from './analytics';
import BraintreeService from './braintreeService';
import getPaymentOptions from './getPaymentOptions';
import { getProductTotal } from './productsService';
import { trackNewVouchers } from './vouchers';

const currencyCode = CURRENCY_CONFIG.code;
const userEmail = ReactCookies.load(COOKIES.userEmail);

const expressBuyService = {
  cardDefault: null,
  currency: currencyCode,
  deviceFingerprintId: '',
  failToCheckout: null,
  instrumentToUse: null,
  isBraintree: false,
  quantity: 0,
  setProgressTest: null,
  success: null,
  threeDSecureInstance: null,
  threeDSRequired: true,
  tokenizationPayload: {},
};

const goToCheckout = (message) => {
  BraintreeService.expressBuy = false;
  expressBuyService.failToCheckout(message || TRY_CHECKOUT);
};

expressBuyService.isCvvRequired = (response) => {
  let cvvRequired = false;

  if (!response) return false;

  const product = get(
    response,
    'apiUserDealInfoVO.apiDealProductsVO.theDealProducts[0]',
    null,
  );
  if (!product) return false;

  // cvv check
  const paymentCvvCheckEnabled = response.paymentCvvCheckEnabled;
  const paymentCvvCheckMaxOrderCostLimitForToday =
    response.paymentCvvCheckMaxOrderCostLimitForToday;
  const paymentCvvCheckMaxItemLimitForToday =
    response.paymentCvvCheckMaxItemLimitForToday;

  if (!paymentCvvCheckEnabled) return false;

  // cvv check user fields
  const data = response.apiUserDealInfoVO;
  expressBuyService.threeDSRequired = data.threeDSAuthenticationRequiredForUser;
  cvvRequired = data.paymentCvvCheckRequiredForUser;
  const initialCvvRequired = data.paymentCvvCheckRequiredForUser;
  const totalOrderCostByUserForToday = data.totalOrderCostByUserForToday;
  const totalOrderItemsByUserForToday = data.totalOrderItemsByUserForToday;

  const quantity = expressBuyService.quantity;
  const price = product.price;
  const totalAmountToPay = quantity * price;

  let cvvSuccessToday = false;
  if (
    (totalOrderCostByUserForToday > paymentCvvCheckMaxOrderCostLimitForToday ||
      totalOrderItemsByUserForToday > paymentCvvCheckMaxItemLimitForToday) &&
    initialCvvRequired === false
  ) {
    // A limit already exceeded, and CVV is false, so has already been used successfully today
    cvvSuccessToday = true;
  }

  // SHOW RULES, ALWAYS ON - over 500 (daily limit) in this order
  if (totalAmountToPay > paymentCvvCheckMaxOrderCostLimitForToday) {
    cvvRequired = true;
  }
  // DAILY LIMITS - order pushes daily total over 500, or pushes daily items over 10
  // don't check if user sucessfully challenged with cvv today
  if (
    !cvvSuccessToday &&
    (totalOrderItemsByUserForToday + quantity >
      paymentCvvCheckMaxItemLimitForToday ||
      totalOrderCostByUserForToday + totalAmountToPay >
        paymentCvvCheckMaxOrderCostLimitForToday)
  ) {
    cvvRequired = true;
  }

  return cvvRequired;
};
const setupPayPal = async (clientError, clientInstance) => {
  if (
    expressBuyService?.instrumentToUse?.type === PAYMENT_TYPES.card &&
    expressBuyService?.threeDSRequired
  ) {
    if (expressBuyService.threeDSecureInstance) {
      expressBuyService.threeDSecureInstance.teardown();
    }
    try {
      const threeDSecureInstance = await threeDSecure.create({
        client: clientInstance,
        version: 2,
      });
      if (threeDSecureInstance) {
        expressBuyService.threeDSecureInstance = threeDSecureInstance;
      }
    } catch {
      logToSplunk(
        {
          email: userEmail,
          event: 'Express buy Error - Express buy could not initialise ',
        },
        'frontend logs - express buy',
      );
      goToCheckout(TRY_CHECKOUT);
    }
  }
  try {
    const dataCollectorInstance = await dataCollector.create({
      client: clientInstance,
      paypal: true,
    });
    if (dataCollectorInstance) {
      // save device data for use in payment
      expressBuyService.dataCollectorInstance = dataCollectorInstance;
      // continue with payment
      await expressBuyService.continueBraintreePayment();
    }
  } catch {
    logToSplunk(
      {
        email: userEmail,
        event: 'Express buy Error - Data collector could not initialise ',
      },
      'frontend logs - express buy',
    );
    goToCheckout(ERROR_MESSAGE);
  }

  try {
    const paypalInstance = await paypal.create({
      client: clientInstance,
    });
    if (paypalInstance) {
      expressBuyService.paypalInstance = paypalInstance;
    }
  } catch {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Express buy Error - Paypal could not initialise ',
      },
      'frontend logs - express buy',
    );
    goToCheckout(ERROR_MESSAGE);
  }
};

const createClient = (tokenResponse) => {
  expressBuyService.clientId = tokenResponse.data.clientId;
  expressBuyService.clientToken = tokenResponse.data.clientToken;
  client.create({ authorization: expressBuyService.clientToken }, setupPayPal);
};

expressBuyService.setupBraintree = async () => {
  try {
    const response = await BraintreeService.getClientToken();
    if (response) {
      createClient(response);
    }
  } catch {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Express buy Error - could not get Client token',
      },
      'frontend logs - express buy',
    );
    goToCheckout(ERROR_MESSAGE);
  }
};

const fetchPaymentOptions = async () => {
  expressBuyService.setProgressTest(PROGRESS_MESSAGE_WAIT);
  try {
    const deal = await getPaymentOptions({
      dealId: expressBuyService.dealId,
      dealVoucherProductId: expressBuyService.dealProductId,
      quantity: expressBuyService.quantity,
    });
    if (deal) {
      expressBuyService.deal = deal;
      expressBuyService.deal.id = expressBuyService.dealId;

      if (deal.paymentDeviceProfilingEnabled) {
        expressBuyService.deviceFingerprintId =
          deal.paymentDeviceProfileVO.sessionId;
        expressBuyService.paymentDeviceProfile = deal.paymentDeviceProfileVO;
      }

      // Is deal express buyable
      if (
        !expressBuyService.deal.isExpressBuyable ||
        expressBuyService.isCvvRequired(expressBuyService.deal)
      ) {
        // Bump out to checkout to allow them to buy normally
        logToSplunk(
          {
            clientId: expressBuyService?.clientId,
            email: userEmail,

            event:
              'Express buy Error - Express buy not posibble or CVV required',
          },
          'frontend logs - express buy',
        );
        goToCheckout(NO_EXPRESS_BUY);

        return;
      }

      expressBuyService.initiatePayment(
        expressBuyService.deal,
        expressBuyService.makePayment,
      );
    }
  } catch {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Express buy Error - Express buy not start payment flow',
      },
      'frontend logs - express buy',
    );
    goToCheckout(TRY_CHECKOUT);
  }
};

const getDefaultCard = async () => {
  try {
    const response = await BraintreeService.getVaultedCards();
    if (response) {
      const cardDefault = response.filter((card) => card.default) || null;
      if (cardDefault !== null) {
        return cardDefault[0].token;
      }

      return null;
    }

    return null;
  } catch {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Express buy Error - Could not get vaulted cards',
      },
      'frontend logs - express buy',
    );
    goToCheckout(GENERIC_ERROR);

    return null;
  }
};

const finalStepPayment = async ({ responseNonce, makePayment }) => {
  if (BraintreeService.paymentMethod === PAYMENT_METHODS.paypal) {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Braintree Payment',
      },
      'frontend logs - express buy',
    );
    makePayment(BraintreeService.makePayment);
  } else {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'OTP Challenge - fired',
      },
      'frontend logs - express buy',
    );
    const payload = await expressBuyService.threeDSecureInstance.verifyCard({
      amount: getProductTotal(expressBuyService.products).toFixed(2).toString(),
      bin: responseNonce.data.target.details.bin,
      merchantName: MERCHANT_NAME,
      nonce: responseNonce.data.target.nonce,
      onLookupComplete: (data, next) => {
        next();
      },
    });
    if (payload) {
      if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
        logToSplunk(
          {
            clientId: expressBuyService?.clientId,
            email: userEmail,
            event: 'OTP Challenge - failed',
            expirationDate: `${payload.details.expirationMonth}/${payload.details.expirationYear}`,
            payload,
            reason: payload.threeDSecureInfo.status,
          },
          'frontend logs - express buy',
        );
        goToCheckout(LIABILITY_ERROR);
      } else {
        logToSplunk(
          {
            clientId: expressBuyService?.clientId,
            email: userEmail,
            event: 'OTP Challenge - successful',
            payload,
          },
          'frontend logs - express buy',
        );
        BraintreeService.nonce = payload.nonce;
        makePayment(BraintreeService.makePayment);
      }
    }
  }
};

const defineMethod = async ({ makePayment }) => {
  if (
    expressBuyService.instrumentToUse.type === 'credit_card' &&
    expressBuyService.threeDSRequired
  ) {
    try {
      const response = await getDefaultCard();
      if (response) {
        const responseNonce = await BraintreeService.getNonce(response);
        if (responseNonce && !responseNonce.data.target.nonce) {
          logToSplunk(
            {
              clientId: expressBuyService?.clientId,
              email: userEmail,
              event: ' Payment Error - Liability would not shift ',
            },
            'frontend logs - express buy',
          );
          goToCheckout(LIABILITY_ERROR);

          return;
        }
        finalStepPayment({ makePayment, responseNonce });
      }
    } catch {
      logToSplunk(
        {
          clientId: expressBuyService?.clientId,
          email: userEmail,
          event: ' Payment Error - Express buy not possible ',
        },
        'frontend logs - express buy',
      );
      goToCheckout(NO_EXPRESS_BUY);
    }
  } else {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: 'Braintree or non 3DS Payment',
      },
      'frontend logs - express buy',
    );
    makePayment(BraintreeService.makePayment);
  }
};

const expressCheckoutBraintree = ({ total, makePayment }) => {
  logToSplunk(
    {
      email: userEmail,
      event: 'Express buy Start',
    },
    'frontend logs - express buy start',
  );
  expressBuyService.isBraintree = true;
  // setup Braintree
  expressBuyService.setupBraintree();
  // the callback after setting up Braintree
  expressBuyService.continueBraintreePayment = async () => {
    BraintreeService.totalAmountToPay = total;
    BraintreeService.products = expressBuyService.products;
    BraintreeService.currency = expressBuyService.currency;
    BraintreeService.deviceData =
      expressBuyService.dataCollectorInstance.deviceData;
    BraintreeService.expressBuy = true;
    BraintreeService.id = expressBuyService.dealId;
    if (expressBuyService.instrumentToUse.type === PAYMENT_TYPES.card) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.card;
    }
    if (expressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal) {
      BraintreeService.paymentMethod = PAYMENT_METHODS.paypal;
    }
    defineMethod({ makePayment });
  };
};

const definePaymentProcessor = ({ total, makePayment }) => {
  if (
    (expressBuyService.instrumentToUse &&
      expressBuyService.instrumentToUse.type === PAYMENT_TYPES.card) ||
    expressBuyService.instrumentToUse.type === PAYMENT_TYPES.paypal
  ) {
    expressCheckoutBraintree({ makePayment, total });
  } else {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: ' Payment Error - Express buy not possible ',
      },
      'frontend logs - express buy',
    );
    goToCheckout(NO_EXPRESS_BUY);
  }
};

expressBuyService.initiatePayment = async (deal, makePayment) => {
  const product = get(
    deal,
    'apiUserDealInfoList[0].apiDealProductsVO.theDealProducts[0]',
    null,
  );

  if (!product) {
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: ' Payment Error - No product available ',
      },
      'frontend logs - express buy',
    );
    goToCheckout(TRY_CHECKOUT);

    return;
  }

  product.quantity = expressBuyService.quantity;
  product.id = product.dealProductId;

  expressBuyService.products = [product];

  // examine deal.paymentOptions.paymentInstruments to see which payment type default 'credit_card' or 'paypal'
  expressBuyService.instrumentToUse = find(
    deal.paymentOptions.paymentInstruments,
    {
      default: 'yes',
    },
  );

  const total = getProductTotal(expressBuyService.products);

  definePaymentProcessor({ makePayment, total });
};

expressBuyService.makePayment = async (paymentFunction) => {
  expressBuyService.setProgressTest(PROGRESS_MESSAGE_PAY);

  try {
    const order = await paymentFunction();
    if (!order.data.productOrder) {
      logToSplunk(
        {
          clientId: expressBuyService?.clientId,
          email: userEmail,
          event: ' Payment Error - No product order ',
        },
        'frontend logs - express buy',
      );
      goToCheckout(GENERIC_ERROR);

      return false;
    }
    order.data.quantity = expressBuyService.quantity;
    order.data.walletAmountUsed = 0;
    order.data.cardType =
      get(
        expressBuyService,
        'deal.paymentOptions.paymentInstruments[0].details.brand',
      ) ?? '';
    if (expressBuyService.isBraintree) {
      order.data.cardType = PAYMENT_METHODS.paypal;
    }
    if (order.config?.data) {
      order.data.orderValue = order.config.data.cashAmount;
    }
    expressBuyService.setProgressTest(PROGRESS_MESSAGE_SUCCESS);
    order.data.currency = currencyCode;

    // Track everything purchase related
    trackPurchase([expressBuyService.deal], order, true);

    const CheckoutOrder = {
      deals: [expressBuyService.deal],
      hasNewOrder: true,
      orderId: order?.data?.productOrder?.id,
      products: expressBuyService.products,
    };
    trackNewVouchers(CheckoutOrder);

    // success - go to my  wowchers page
    expressBuyService.success(order?.data?.productOrder?.id);

    return true;
  } catch {
    expressBuyService.setProgressTest(PROGRESS_MESSAGE_FAILED);
    logToSplunk(
      {
        clientId: expressBuyService?.clientId,
        email: userEmail,
        event: ' Express buy Error - Error in the order ',
      },
      'frontend logs - express buy',
    );
    goToCheckout(GENERIC_ERROR);

    return false;
  }
};

expressBuyService.initExpressBuy = (deal, quantity) => {
  expressBuyService.quantity = quantity;
  expressBuyService.dealId = deal.id;
  expressBuyService.dealProductId = deal.products[0].id;
  expressBuyService.deviceFingerprintId = '';
  fetchPaymentOptions();
};

export default expressBuyService;
